import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, TrendingUp, Award } from "lucide-react";
import Seo, { NAGPUR_AREAS, breadcrumbJsonLd } from "../components/Seo";
import { api } from "../lib/api";
import PropertyCard from "../components/PropertyCard";

/**
 * SEO-targeted landing page for popular Nagpur / Maharashtra areas.
 * URL: /property-in-nagpur/:area  e.g. /property-in-nagpur/wardha-road
 */
export default function AreaLandingPage() {
    const { area } = useParams();
    const meta = NAGPUR_AREAS.find((a) => a.slug === area);
    const [props, setProps] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!meta) return;
        // Match either the area name in locality OR the city field, case-insensitive
        setLoading(true);
        api.get("/properties", { params: { city: "Nagpur", search: meta.name, limit: 24 } })
            .then((r) => setProps(r.data?.items || []))
            .finally(() => setLoading(false));
    }, [meta]);

    if (!meta) return <Navigate to="/properties" replace />;

    const title = `Properties & Plots in ${meta.name}, Nagpur`;
    const description = `Find verified plots, flats, NA plots and commercial properties in ${meta.name}, Nagpur — ${meta.desc}. RERA-verified listings with Global Property Mart.`;
    const keywords = [
        `property in ${meta.name}`,
        `plots in ${meta.name}`,
        `${meta.name} Nagpur property`,
        `buy plot in ${meta.name}`,
        `${meta.name} real estate`,
        "plots in Nagpur",
        "property in Nagpur",
        "buy plot in Nagpur",
        "real estate company in Nagpur",
        "property in Maharashtra",
    ].join(", ");

    return (
        <div className="min-h-screen bg-navy-900">
            <Seo
                title={title}
                description={description}
                keywords={keywords}
                path={`/property-in-nagpur/${meta.slug}`}
                jsonLd={breadcrumbJsonLd([
                    { name: "Home", path: "/" },
                    { name: "Property in Nagpur", path: "/property-in-nagpur" },
                    { name: meta.name, path: `/property-in-nagpur/${meta.slug}` },
                ])}
            />

            {/* HERO */}
            <section className="text-on-image relative bg-gradient-to-br from-navy-950 to-navy-900 border-b border-gold-500/10">
                <div className="absolute inset-0 grid-pattern opacity-25" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20">
                    <Link to="/properties" className="text-xs text-gold-500 inline-flex items-center gap-2 mb-4 hover:text-gold-100">
                        <MapPin className="w-3.5 h-3.5" /> All Areas in Nagpur
                    </Link>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gold-500/10 border border-gold-500/40 mb-5">
                        <Award className="w-3.5 h-3.5 text-gold-500" />
                        <span className="text-xs font-semibold text-gold-500 tracking-wider uppercase">RERA-Verified Properties</span>
                    </div>
                    <h1 className="font-heading text-4xl lg:text-6xl font-bold text-white tracking-tight">
                        Properties in <span className="text-gold-500">{meta.name}</span>, Nagpur
                    </h1>
                    <p className="text-white/70 mt-5 max-w-3xl text-lg leading-relaxed">
                        Explore verified plots, flats and commercial properties in {meta.name} — {meta.desc}. Backed by India's most trusted real estate ecosystem.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-7">
                        <Link to={`/properties?city=Nagpur&search=${encodeURIComponent(meta.name)}`} data-testid="area-cta-search" className="px-5 py-3 rounded-md bg-gold-500 text-navy-900 font-semibold inline-flex items-center gap-2 hover:bg-gold-100">
                            Browse {meta.name} Properties <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/brokers" className="px-5 py-3 rounded-md border border-gold-500/40 text-gold-500 font-semibold hover:bg-gold-500/10">
                            Talk to a Local Broker
                        </Link>
                    </div>
                </div>
            </section>

            {/* WHY INVEST CARDS */}
            <section className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <Card icon={TrendingUp} title="High Investment Potential" body={`${meta.name} is one of Nagpur's most actively appreciating real estate corridors.`} />
                    <Card icon={ShieldCheck} title="100% Verified Listings" body="Every property listed on Global Property Mart is RERA-checked and document-verified by our advocates." />
                    <Card icon={MapPin} title="Local Brokers On-Ground" body={`Connect directly with our verified local network of brokers active in ${meta.name}.`} />
                </div>
            </section>

            {/* PROPERTIES LISTED */}
            <section className="max-w-7xl mx-auto px-6 lg:px-8 pb-20">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="label-overline mb-1">Listings</div>
                        <h2 className="font-heading text-3xl font-bold text-white">Current properties in {meta.name}</h2>
                    </div>
                    <Link to={`/properties?city=Nagpur&search=${encodeURIComponent(meta.name)}`} className="text-sm text-gold-500 inline-flex items-center gap-1 hover:text-gold-100">View all <ArrowRight className="w-3 h-3" /></Link>
                </div>
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="aspect-[4/3] bg-navy-800 rounded-2xl animate-pulse" />
                        ))}
                    </div>
                ) : props.length === 0 ? (
                    <div className="rounded-2xl bg-navy-800 border border-white/5 p-12 text-center">
                        <div className="text-white/55 mb-3">No active listings in {meta.name} right now.</div>
                        <Link to="/properties" className="text-gold-500 font-semibold hover:text-gold-100 inline-flex items-center gap-1">
                            Browse all Nagpur properties <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {props.map((p) => <PropertyCard key={p.id} p={p} />)}
                    </div>
                )}
            </section>

            {/* NEARBY AREAS */}
            <section className="bg-navy-950 border-t border-white/5 py-16">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="label-overline mb-2">Explore Other Areas</div>
                    <h2 className="font-heading text-3xl font-bold text-white mb-8">Popular Real Estate Areas in Nagpur</h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {NAGPUR_AREAS.filter((a) => a.slug !== meta.slug).map((a) => (
                            <Link
                                key={a.slug}
                                to={`/property-in-nagpur/${a.slug}`}
                                className="rounded-xl bg-navy-800 border border-white/5 p-4 hover:border-gold-500/40 group transition-colors"
                                data-testid={`area-link-${a.slug}`}
                            >
                                <div className="font-semibold text-white group-hover:text-gold-500 transition-colors">{a.name}</div>
                                <div className="text-[11px] text-white/50 mt-1 line-clamp-2">{a.desc}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}

function Card({ icon: Icon, title, body }) {
    return (
        <div className="rounded-2xl bg-navy-800 border border-gold-500/20 p-6">
            <div className="w-10 h-10 rounded-xl bg-gold-500/10 text-gold-500 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5" />
            </div>
            <div className="font-heading text-lg font-bold text-white">{title}</div>
            <div className="text-sm text-white/65 mt-2 leading-relaxed">{body}</div>
        </div>
    );
}
